"use client";

import { c as _c } from "react/compiler-runtime";
import type React from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ToastInfoIcon from "@/assets/svg/toast-info.svg";
import ToastSuccessIcon from "@/assets/svg/toast-success.svg";
export type ToastType = "success" | "error" | "warning" | "info";
export interface ToastOptions {
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}
export interface ToastData {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// 全局状态管理器
class ToastManager {
  private toasts: ToastData[] = [];
  private listeners: Set<(toasts: ToastData[]) => void> = new Set();
  private maxToasts: number = 1;
  subscribe(listener: (toasts: ToastData[]) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  private notify() {
    // 创建新的数组引用，确保React能检测到变化
    const toastsCopy = [...this.toasts];
    this.listeners.forEach(listener => {
      listener(toastsCopy);
    });
  }
  show(type: ToastType, message: string, options: ToastOptions = {}) {
    // 检查是否已经存在相同的toast
    const existingToast = this.toasts.find(toast => toast.message === message && toast.type === type);
    if (existingToast) {
      return existingToast.id;
    }
    const id = Math.random().toString(36).substr(2, 9);
    const duration = options.duration || 4000;
    const newToast: ToastData = {
      id,
      type,
      message,
      duration,
      action: options.action
    };

    // 添加新toast
    this.toasts.push(newToast);

    // 如果超过最大数量，移除最旧的toast
    if (this.toasts.length > this.maxToasts) {
      this.toasts = this.toasts.slice(-this.maxToasts);
    }
    this.notify();
    setTimeout(() => {
      this.dismiss(id);
    }, duration);
    return id;
  }
  dismiss(id?: string) {
    if (id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id);
    } else {
      this.toasts = [];
    }
    this.notify();
  }
  getToasts() {
    return this.toasts;
  }

  // 设置最大toast数量
  setMaxToasts(max: number) {
    this.maxToasts = max;
    // 如果当前toast数量超过新的限制，移除多余的
    if (this.toasts.length > this.maxToasts) {
      this.toasts = this.toasts.slice(-this.maxToasts);
      this.notify();
    }
  }

  // 获取当前最大toast数量
  getMaxToasts() {
    return this.maxToasts;
  }
}

// 全局实例
const toastManager = new ToastManager();

// 单个Toast组件
const ToastItem: React.FC<{
  toast: ToastData;
  index: number;
}> = t0 => {
  const $ = _c(34);
  const {
    toast,
    index
  } = t0;
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  let t1;
  if ($[0] !== toast.type) {
    t1 = () => {
      switch (toast.type) {
        case "success":
          {
            return <ToastSuccessIcon className="w-6 h-6" />;
          }
        case "error":
          {
            return <ToastInfoIcon className="w-6 h-6" />;
          }
        case "warning":
          {
            return <ToastInfoIcon className="w-6 h-6" />;
          }
        case "info":
          {
            return <ToastInfoIcon className="w-6 h-6" />;
          }
        default:
          {
            return null;
          }
      }
    };
    $[0] = toast.type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getIcon = t1;
  let t2;
  if ($[2] !== toast.type) {
    t2 = () => {
      switch (toast.type) {
        case "success":
          {
            return "border-l-[#00A942]";
          }
        case "error":
          {
            return "border-l-[#000000]";
          }
        case "warning":
          {
            return "border-l-[#000000]";
          }
        case "info":
          {
            return "border-l-[#000000]";
          }
        default:
          {
            return "border-l-gray-300";
          }
      }
    };
    $[2] = toast.type;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getBorderColor = t2;
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    };
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== toast.id) {
    t5 = () => {
      setIsLeaving(true);
      setTimeout(() => {
        toastManager.dismiss(toast.id);
      }, 300);
    };
    $[6] = toast.id;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleDismiss = t5;
  const t6 = `
        hidden md:block
        min-w-80 max-w-125 bg-white ${getBorderColor()} border-l-4
        shadow-[0px_2px_8px_rgba(0,0,0,0.1)]
        transition-all duration-400 ease-out 
        ${isVisible && !isLeaving ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full"}
        ${isLeaving ? "opacity-0 translate-x-full" : ""}
        ${index > 0 ? "mt-3" : ""}
      `;
  let t7;
  if ($[8] !== getIcon) {
    t7 = getIcon();
    $[8] = getIcon;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <div className="flex-shrink-0 mr-3">{t7}</div>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== toast.message) {
    t9 = <div className="flex-1 min-w-0 font-['DINPro-Regular'] text-[#666666] text-sm break-words">{toast.message}</div>;
    $[12] = toast.message;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== toast.action) {
    t10 = toast.action && <button type="button" onClick={e => {
      e.stopPropagation();
      toast.action?.onClick();
    }} className="ml-3 text-xs text-black underline hover:cursor-pointer hover:font-extrabold transition-all font-['DINPro-Regular'] flex-shrink-0">{toast.action.label}</button>;
    $[14] = toast.action;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8 || $[18] !== t9) {
    t11 = <div className="flex items-start px-4 py-4">{t8}{t9}{t10}</div>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== handleDismiss || $[21] !== t11 || $[22] !== t6) {
    t12 = <div className={t6} onClick={handleDismiss}>{t11}</div>;
    $[20] = handleDismiss;
    $[21] = t11;
    $[22] = t6;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = `
        block md:hidden
        fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[9999]
        text-white cursor-pointer
        px-4 py-3 text-sm text-center
        transition-all duration-300 ease-out
        max-w-[14.25rem] min-w-[8.5rem] w-max
        ${isVisible && !isLeaving ? "opacity-100 scale-100" : "opacity-0 scale-95"}
        ${isLeaving ? "opacity-0 scale-95" : ""}
      `;
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      backgroundColor: "rgba(16, 16, 21, 0.8)"
    };
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== toast.message) {
    t15 = <div className="font-['DINPro-Regular'] leading-tight">{toast.message}</div>;
    $[25] = toast.message;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== handleDismiss || $[28] !== t13 || $[29] !== t15) {
    t16 = <div className={t13} style={t14} onClick={handleDismiss}>{t15}</div>;
    $[27] = handleDismiss;
    $[28] = t13;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t12 || $[32] !== t16) {
    t17 = <>{t12}{t16}</>;
    $[31] = t12;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  return t17;
};
export const PopToast: React.FC = () => {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [toasts, setToasts] = useState(t0);
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setMounted(true);
      const unsubscribe = toastManager.subscribe(newToasts => {
        setToasts(newToasts);
      });
      return () => {
        unsubscribe();
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!mounted || typeof window === "undefined") {
    return null;
  }
  let t3;
  if ($[3] !== toasts) {
    t3 = toasts.map(_temp);
    $[3] = toasts;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = createPortal(<div className="fixed top-[72px] right-3 z-[9999] flex flex-col gap-3">{t3}</div>, document.body);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};

// 导出toast管理器供外部使用
export { toastManager };
function _temp(toast, index) {
  return <ToastItem key={toast.id} toast={toast} index={index} />;
}